import React from "react";
import { ApiError } from "../../api/apiFetch";
import { UserMeetingResource } from "../../api/user_meetings";
import { buildJoinTarget } from "./joinTarget";

/**
 * Dropping into a session from the Video Platform list.
 *
 * Opens the session in a new tab so the panel stays behind — an admin joining a
 * room is usually in the middle of managing it. The tab has to be opened
 * SYNCHRONOUSLY, before the resolver call is awaited: by the time the answer
 * arrives the click's user-gesture window has closed, and a `window.open` then
 * is exactly what a popup blocker suppresses, silently. So the blank tab is
 * claimed first and pointed at the destination once it is known; if the browser
 * refused it anyway, the join falls back to this tab rather than doing nothing.
 */

const GENERIC_JOIN_ERROR = "Couldn't open that session.";

interface UseJoinMeetingArgs {
  onError: (message: string) => void;
}

interface UseJoinMeetingReturn {
  /**
   * @param meetingId the row's `id` — NOT `externalMeetingId`. `id` is what the
   *   resolver accepts, and it is also what makes a continuous-room segment
   *   resolve to the day room it belongs to.
   */
  join: (meetingId: string, options: { asHost: boolean }) => Promise<void>;
  /** The row whose join is being resolved, if any. */
  joiningMeetingId: string | null;
}

export function useJoinMeeting({
  onError,
}: UseJoinMeetingArgs): UseJoinMeetingReturn {
  const [joiningMeetingId, setJoiningMeetingId] = React.useState<string | null>(
    null
  );
  /**
   * Which join is the current one. Two rows can be clicked in succession, and
   * the first to settle is not necessarily the first to have started — clearing
   * the marker unconditionally would re-enable a row whose own join is still in
   * flight, and a second click there claims a second tab and records a second
   * join event.
   */
  const joinSeqRef = React.useRef(0);

  const join = React.useCallback(
    async (meetingId: string, { asHost }: { asHost: boolean }) => {
      // Must precede the await — see the note above.
      const tab = window.open("", "_blank");
      const seq = ++joinSeqRef.current;
      setJoiningMeetingId(meetingId);

      try {
        const { joinUrl } = await UserMeetingResource.resolveJoinUrl(meetingId);
        const target = buildJoinTarget(joinUrl, { asHost });

        if (!target) {
          tab?.close();
          onError(GENERIC_JOIN_ERROR);
          return;
        }

        if (tab) {
          // Cut the opener link while the tab is still about:blank — still
          // same-origin, so still ours to change. Worth cutting: the
          // destination can be a host an admin typed into the fallback field,
          // validated nowhere, and an opener handle lets that page navigate
          // this panel.
          tab.opener = null;
          tab.location.href = target;
        } else {
          window.location.assign(target);
        }
      } catch (err) {
        tab?.close();
        // 409 arrives as { message } ("Meeting has ended"); 403 and 404 arrive
        // as a bare JSON string ("Access denied", "Meeting not found"), which
        // apiFetch also unwraps onto ApiError.message.
        onError(
          err instanceof ApiError && err.message
            ? err.message
            : GENERIC_JOIN_ERROR
        );
      } finally {
        // Only the newest join owns the marker.
        if (joinSeqRef.current === seq) setJoiningMeetingId(null);
      }
    },
    [onError]
  );

  return { join, joiningMeetingId };
}
